// Состояние выезжающей панели чата: открыта ли, какой тред, с какой страницы открыта.
import type { ModelChoice } from '@autoaff/protocol';
import { useSyncExternalStore } from 'react';
import type { Ask } from '../shell/waiting';

export interface PageContext {
  kind: string;
  label: string;
  id?: string;
}

interface ChatState {
  open: boolean;
  threadId: string | null;
  context: PageContext | null;
  /** Текст, который нужно сразу отправить (например, из палитры команд). */
  draft: string | null;
  /** Модель, выбранная для нового чата до первого сообщения. */
  draftChoice: ModelChoice | null;
  /**
   * Вопрос агента, ради ответа на который открыт тред фоновой работы: закреплён сверху, первое сообщение — ответ
   * на него. Живёт, пока открыт тот же тред.
   */
  ask: Ask | null;
}

let state: ChatState = {
  open: false,
  threadId: null,
  context: null,
  draft: null,
  draftChoice: null,
  ask: null,
};
const listeners = new Set<() => void>();
const set = (patch: Partial<ChatState>) => {
  state = { ...state, ...patch };
  for (const l of listeners) l();
};

export const chatStore = {
  get: () => state,
  open(opts: { threadId?: string | null; draft?: string | null; ask?: Ask | null } = {}) {
    const threadId = opts.ask ? opts.ask.threadId : opts.threadId;
    const next = threadId !== undefined ? threadId : state.threadId;
    set({
      open: true,
      ...(threadId !== undefined ? { threadId } : {}),
      draft: opts.draft ?? null,
      // Тот же тред без нового вопроса — закреплённый вопрос остаётся
      ask: opts.ask !== undefined ? opts.ask : state.ask?.threadId === next ? state.ask : null,
    });
  },
  close() {
    set({ open: false });
  },
  setThread(threadId: string | null) {
    const ask = state.ask?.threadId === threadId ? state.ask : null;
    set(threadId === null ? { threadId, draftChoice: null, ask } : { threadId, ask });
  },
  /** Ответ на закреплённый вопрос ушёл. */
  answered() {
    if (state.ask) set({ ask: { ...state.ask, answered: true } });
  },
  setDraftChoice(draftChoice: ModelChoice | null) {
    set({ draftChoice });
  },
  setContext(context: PageContext | null) {
    if (state.context?.label !== context?.label) set({ context });
  },
  takeDraft(): string | null {
    const d = state.draft;
    if (d) set({ draft: null });
    return d;
  },
};

export function useChat(): ChatState {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => state,
  );
}
